import { z } from 'zod';
// ============================================================================
// ORGANIZATION MODEL - New Schema
// Organization -> Tenant -> Business Models hierarchy
// ============================================================================
// Organization Schema - Parent entity that contains Tenants
export const OrganizationSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(255),
    slug: z.string().min(1).max(100),
    logo: z.string().url().optional(),
    website: z.string().url().optional(),
    timezone: z.string().default('Asia/Kolkata'),
    currency: z.string().length(3).default('INR'),
    isActive: z.boolean().default(true),
    isDeleted: z.boolean().default(false),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().optional(),
    createdById: z.string().uuid().optional(),
    updatedById: z.string().uuid().optional(),
});
// Tenant Type Enum
export var TenantType;
(function (TenantType) {
    TenantType["SCHOOL"] = "SCHOOL";
    TenantType["SALES_COMPANY"] = "SALES_COMPANY";
    TenantType["HYBRID"] = "HYBRID";
})(TenantType || (TenantType = {}));
// Tenant Schema - Child entity under Organization
export const TenantSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    name: z.string().min(1).max(255),
    type: z.nativeEnum(TenantType),
    code: z.string().min(1).max(50), // Unique within organization
    slug: z.string().min(1).max(100), // Unique within organization
    // Denormalized from organization for performance
    orgName: z.string().optional(),
    orgCurrency: z.string().optional(),
    // References to normalized entities
    addressId: z.string().uuid().optional(),
    primaryContactId: z.string().uuid().optional(),
    logo: z.string().url().optional(),
    isActive: z.boolean().default(true),
    isDeleted: z.boolean().default(false),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().optional(),
    createdById: z.string().uuid().optional(),
    updatedById: z.string().uuid().optional(),
    version: z.number().default(1), // For optimistic locking
});
// Subscription Status Enum
export var SubscriptionStatus;
(function (SubscriptionStatus) {
    SubscriptionStatus["TRIAL"] = "TRIAL";
    SubscriptionStatus["ACTIVE"] = "ACTIVE";
    SubscriptionStatus["PAST_DUE"] = "PAST_DUE";
    SubscriptionStatus["CANCELED"] = "CANCELED";
    SubscriptionStatus["EXPIRED"] = "EXPIRED";
})(SubscriptionStatus || (SubscriptionStatus = {}));
// Subscription Schema - Separate from Organization
export const SubscriptionSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    plan: z.string().min(1).max(50),
    status: z.nativeEnum(SubscriptionStatus).default(SubscriptionStatus.TRIAL),
    currentPeriodStart: z.date(),
    currentPeriodEnd: z.date(),
    trialStart: z.date().optional(),
    trialEnd: z.date().optional(),
    // Limits at organization level
    maxTenants: z.number().default(1),
    maxUsersTotal: z.number().default(50),
    maxStudentsTotal: z.number().default(200),
    storageQuotaGB: z.number().default(10),
    createdAt: z.date(),
    updatedAt: z.date(),
    version: z.number().default(1),
});
// Tenant Feature Schema - Flexible feature management
export const TenantFeatureSchema = z.object({
    tenantId: z.string().uuid(),
    feature: z.string().min(1).max(100),
    isEnabled: z.boolean().default(true),
    config: z.record(z.unknown()).optional(), // JSONB field
    createdAt: z.date(),
    updatedAt: z.date(),
});
// Create/Update Schemas
export const CreateOrganizationSchema = OrganizationSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
    isDeleted: true,
});
export const UpdateOrganizationSchema = CreateOrganizationSchema.partial();
export const CreateTenantSchema = TenantSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
    isDeleted: true,
    version: true,
});
export const UpdateTenantSchema = CreateTenantSchema.partial();
// Helper functions
export function isTenantSchool(tenant) {
    return tenant.type === TenantType.SCHOOL || tenant.type === TenantType.HYBRID;
}
export function isTenantSales(tenant) {
    return tenant.type === TenantType.SALES_COMPANY || tenant.type === TenantType.HYBRID;
}
export function isSubscriptionActive(subscription) {
    return subscription.status === SubscriptionStatus.ACTIVE ||
        subscription.status === SubscriptionStatus.TRIAL;
}
export function isWithinSubscriptionLimits(subscription, currentTenants, currentUsers) {
    return currentTenants <= subscription.maxTenants &&
        currentUsers <= subscription.maxUsersTotal;
}
//# sourceMappingURL=Organization.js.map